import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ApiError } from '@shared/api/api-error';

/** Cuantas veces reintentar antes de rendirse en un fallo transitorio. */
const MAX_RETRIES = 2;

/**
 * Datos considerados frescos durante 30 s.
 *
 * Es un compromiso deliberado: en logistica el stock cambia constantemente,
 * pero refetchear en cada montaje de componente multiplica la carga sobre
 * PostgreSQL sin que el operario perciba la diferencia. Las pantallas que
 * necesitan exactitud al segundo -picking, despacho- bajan su propio
 * staleTime; no se sube el global por ellas.
 */
const DEFAULT_STALE_TIME_MS = 30_000;

function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: DEFAULT_STALE_TIME_MS,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // Un 4xx no se reintenta: la peticion es invalida o no autorizada y
          // repetirla solo suma carga y ruido en los logs.
          if (error instanceof ApiError && !error.isRetryable) return false;
          return failureCount < MAX_RETRIES;
        },
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
      },
      mutations: {
        // Las mutaciones NUNCA se reintentan automaticamente: repetir una
        // aprobacion o una salida de stock duplicaria el efecto. El reintento
        // seguro exige clave de idempotencia y lo decide cada caso (spec 36).
        retry: false,
      },
    },
  });
}

export function QueryProvider({ children }: { children: ReactNode }) {
  // useState y no una constante de modulo: en tests cada render monta un
  // cliente limpio y la cache no se filtra de un caso al siguiente.
  const [client] = useState(createQueryClient);

  return <QueryClientProvider client={client}>{children}</QueryClientProvider>;
}
